import {
  isValidEmail,
  isValidVietnamesePhone,
  generateOrderNote,
  generateOrderId,
  // getCountryCode,
  parseVNPayResponse,
  formatToURLEncoded,
} from "./CheckoutHelpers";

/**
 * Enhanced validation with detailed error messages
 */
export const validateCheckoutData = (formData, cartItems) => {
  const errors = {};

  // Required fields validation
  const requiredFields = {
    email: "Email address",
    phone: "Phone number",
    firstName: "First name",
    lastName: "Last name",
    address: "Address",
    city: "City",
    state: "State",
    ward: "Ward",
    zipCode: "ZIP code",
  };

  for (const [field, label] of Object.entries(requiredFields)) {
    if (!formData[field] || formData[field].trim() === "") {
      errors[field] = `${label} is required.`;
    }
  }

  // Email validation
  if (formData.email && !isValidEmail(formData.email)) {
    errors.email = "Please enter a valid email address.";
  }

  // Phone validation
  if (formData.phone && !isValidVietnamesePhone(formData.phone)) {
    errors.phone = "Please enter a valid Vietnamese phone number.";
  }

  // Cart validation
  if (!cartItems || cartItems.length === 0) {
    errors.cart = "Your cart is empty.";
  }

  // Shipping validation (if not using billing address)
  if (!formData.useMyInfo) {
    const shippingFields = {
      shippingAddress: "Shipping address",
      shippingCity: "Shipping city",
      shippingState: "Shipping state",
      shippingWard: "Shipping ward",
      shippingZipCode: "Shipping ZIP code",
    };

    for (const [field, label] of Object.entries(shippingFields)) {
      if (!formData[field] || formData[field].trim() === "") {
        errors[field] = `${label} is required.`;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    message:
      Object.keys(errors).length > 0
        ? Object.values(errors)[0]
        : "Validation passed.",
  };
};

/**
 * Prepare order data for VNPay payment method
 */
const getExplicitCustomerId = (formData) => {
  if (
    !Object.prototype.hasOwnProperty.call(formData || {}, "customer_id") ||
    formData.customer_id === undefined ||
    formData.customer_id === null ||
    formData.customer_id === ""
  ) {
    throw new Error(getCheckoutErrorMessage("missing_customer_id"));
  }

  const customerId = Number(formData.customer_id);
  if (Number.isNaN(customerId) || customerId < 0) {
    throw new Error(getCheckoutErrorMessage("invalid_customer_id"));
  }

  if (formData.isGuestCheckout === true && customerId !== 0) {
    throw new Error(getCheckoutErrorMessage("guest_customer_id_must_be_zero"));
  }

  if (formData.isGuestCheckout === false && customerId <= 0) {
    throw new Error(getCheckoutErrorMessage("invalid_customer_id"));
  }

  return customerId;
};

export const prepareVNPayOrderData = (
  formData,
  cartItems,
  orderTotals,
  locationData = {}
) => {
  const {
    selectedProvince,
    selectedDistrict,
    selectedWard,
    shippingLocation = {},
  } = locationData;

  // Validate required data
  if (!cartItems || cartItems.length === 0) {
    throw new Error("Cart is empty");
  }

  if (!formData.firstName || !formData.lastName || !formData.email) {
    throw new Error("Customer information is incomplete");
  }

  const orderData = {
    customer_id: getExplicitCustomerId(formData),

    client_totals: {
      subtotal: Number(orderTotals?.subtotal || 0),
      shipping_total: Number(orderTotals?.shippingCost || 0),
      discount_total: Number(orderTotals?.discountTotal || 0),
      tax_total: Number(orderTotals?.taxTotal || 0),
      total: Number(orderTotals?.total || 0),
    },

    // Items - VNPay format
    items: cartItems.map((item) => ({
      product_id: parseInt(item.product_id || item.id),
      quantity: parseInt(item.quantity) || 1,
      ...(item.variation_id && { variation_id: parseInt(item.variation_id) }),
    })),

    // Customer information
    customer: {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      email: formData.email || "",
      phone: formData.phone || "",
    },

    // Billing address - VNPay format
    billing_address: {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      address_1: formData.address || "",
      ward_name: selectedWard?.name || formData.ward || "",
      ward_code: selectedWard?.id || formData?.shippingWardId || "",
      district_name: selectedDistrict?.name || formData.state || "",
      district_id: selectedDistrict?.id || formData?.districtId || "",
      city: selectedProvince?.name || formData.city || "",
      state: "",
      postcode: formData.zipCode || "",
      country: "VN",
      email: formData.email || "",
      phone: formData.phone || "",
    },

    // Shipping address - VNPay format
    shipping_address: {
      first_name: formData.useMyInfo
        ? formData.firstName || ""
        : formData.shippingFirstName || formData.firstName || "",
      last_name: formData.useMyInfo
        ? formData.lastName || ""
        : formData.shippingLastName || formData.lastName || "",
      address_1: formData.useMyInfo
        ? formData.address || ""
        : formData.shippingAddress || formData.address || "",
      ward_name: formData.useMyInfo
        ? selectedWard?.name || formData.ward || ""
        : shippingLocation?.selectedWard?.name || formData.shippingWard || "",
      ward_code: formData.useMyInfo
        ? selectedWard?.id || ""
        : shippingLocation?.selectedWard?.id || "",
      district_name: formData.useMyInfo
        ? selectedDistrict?.name || formData.state || ""
        : shippingLocation?.selectedDistrict?.name ||
          formData.shippingState ||
          "",
      district_id: formData.useMyInfo
        ? selectedDistrict?.id || ""
        : shippingLocation?.selectedDistrict?.id || "",
      city: formData.useMyInfo
        ? selectedProvince?.name || formData.city || ""
        : shippingLocation?.selectedProvince?.name ||
          formData.shippingCity ||
          formData.city ||
          "",
      state: "",
      postcode: formData.useMyInfo
        ? formData.zipCode || ""
        : formData.shippingZipCode || formData.zipCode || "",
      country: "VN",
    },

    // Shipping cost and method
    // shipping_method: formData.shippingMethod || "flat_rate",
    shipping_lines: orderTotals.shippingCost
      ? [
          {
            method_id: formData.shippingMethod || "flat_rate",
            method_title: "Shipping",
            total: orderTotals.shippingCost.toString(),
          },
        ]
      : [],
    // Payment method - always vnpay for this function
    payment_method: "vnpay",

    // Customer note
    customer_note: formData.orderComments || generateOrderNote(formData),
  };

  return orderData;
};

/**
 * Prepare order data for WooCommerce COD payment method
 */
export const prepareCODOrderData = (
  formData,
  cartItems,
  orderTotals,
  locationData = {}
) => {
  const {
    selectedProvince,
    selectedDistrict,
    selectedWard,
    shippingLocation = {},
  } = locationData;

  // Validate required data
  if (!cartItems || cartItems.length === 0) {
    throw new Error("Cart is empty");
  }

  if (!formData.firstName || !formData.lastName || !formData.email) {
    throw new Error("Customer information is incomplete");
  }

  const orderData = {
    // Payment method - MUST be set first
    payment_method: "cod",

    // Set initial status for COD
    status: "pending",

    // Line items - WooCommerce format
    line_items: cartItems.map((item) => ({
      product_id: parseInt(item.product_id || item.id),
      quantity: parseInt(item.quantity) || 1,
      ...(item.variation_id && { variation_id: parseInt(item.variation_id) }),
      ...(item.checkout_source === "cart" &&
        item.cart_item_key && { cart_item_key: item.cart_item_key }),
    })),

    // Billing address - WooCommerce format
    billing: {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      address_1: formData.address || "",
      address_2: selectedWard.name || formData.ward || "",
      city: selectedProvince?.name || formData.city || "",
      state: selectedDistrict?.name || formData.state || "",
      postcode: formData.zipCode || "",
      country: "VN",
      email: formData.email || "",
      phone: formData.phone || "",
    },

    // Shipping address - WooCommerce format
    shipping: {
      first_name: formData.useMyInfo
        ? formData.firstName || ""
        : formData.shippingFirstName || formData.firstName || "",
      last_name: formData.useMyInfo
        ? formData.lastName || ""
        : formData.shippingLastName || formData.lastName || "",
      address_1: formData.useMyInfo
        ? formData.address || ""
        : formData.shippingAddress || formData.address || "",
      address_2: formData.useMyInfo
        ? selectedWard?.name || formData.ward || ""
        : shippingLocation?.selectedWard?.name ||
          formData.shippingWard ||
          formData.ward ||
          "",
      city: formData.useMyInfo
        ? selectedProvince?.name || formData.city || ""
        : shippingLocation?.selectedProvince?.name ||
          formData.shippingCity ||
          formData.city ||
          "",
      state: formData.useMyInfo
        ? selectedDistrict?.name || formData.state || ""
        : shippingLocation?.selectedDistrict?.name ||
          formData.shippingState ||
          "",
      postcode: formData.useMyInfo
        ? formData.zipCode || ""
        : formData.shippingZipCode || formData.zipCode || "",
      country: "VN",
    },

    // Shipping lines
    shipping_lines: orderTotals.shippingCost
      ? [
          {
            method_id: formData.shippingMethod || "flat_rate",
            method_title: "Shipping",
            total: orderTotals.shippingCost.toString(),
          },
        ]
      : [],

    // Customer note
    customer_note: formData.orderComments || generateOrderNote(formData),

    // Ensure customer email is set
    customer_id: getExplicitCustomerId(formData),

    client_totals: {
      subtotal: Number(orderTotals?.subtotal || 0),
      shipping_total: Number(orderTotals?.shippingCost || 0),
      discount_total: Number(orderTotals?.discountTotal || 0),
      tax_total: Number(orderTotals?.taxTotal || 0),
      total: Number(orderTotals?.total || 0),
    },

    // Meta data for debugging
    meta_data: [
      {
        key: "_checkout_source",
        value: "react_app",
      },
      {
        key: "_ward_name",
        value: selectedWard?.name || formData.ward || "",
      },
      {
        key: "_ward_code",
        value: selectedWard?.id || "",
      },
      {
        key: "ward_code",
        value: selectedWard?.id || "",
      },
      {
        key: "_district_name",
        value: selectedDistrict?.name || formData.state || "",
      },
      {
        key: "_district_id",
        value: selectedDistrict?.id || "",
      },
      {
        key: "district_id",
        value: selectedDistrict?.id || "",
      },
      {
        key: "_shipping_ward",
        value: selectedWard?.name || formData.ward || "",
      },
      {
        key: "_shipping_district",
        value: selectedDistrict?.name || formData.state || "",
      },
      {
        key: "_province_name",
        value: selectedProvince?.name || formData.city || "",
      },
      {
        key: "_province_id",
        value: selectedProvince?.id || "",
      },
    ],
  };

  return orderData;
};

/**
 * Enhanced order data preparation - Legacy function (deprecated)
 * Use prepareVNPayOrderData or prepareCODOrderData instead
 */
export const prepareOrderData = (
  formData,
  cartItems,
  orderTotals,
  locationData = {}
) => {
  if (formData.paymentMethod === "cod") {
    return prepareCODOrderData(formData, cartItems, orderTotals, locationData);
  } else {
    return prepareVNPayOrderData(
      formData,
      cartItems,
      orderTotals,
      locationData
    );
  }
  // Default to COD format for backward compatibility
};

/**
 * Prepare order data based on payment method automatically
 */
export const prepareOrderDataByPaymentMethod = (
  formData,
  cartItems,
  orderTotals,
  locationData = {}
) => {
  const paymentMethod = formData.paymentMethod;

  // Determine which payload format to use
  switch (paymentMethod) {
    case "cod":
      return prepareCODOrderData(
        formData,
        cartItems,
        orderTotals,
        locationData
      );

    case "vnpay_visa":
    case "vnpay":
      return prepareVNPayOrderData(
        formData,
        cartItems,
        orderTotals,
        locationData
      );

    case "paypal":
      // PayPal might use different format, for now use VNPay format
      return prepareVNPayOrderData(
        formData,
        cartItems,
        orderTotals,
        locationData
      );

    default:
      // Default to VNPay format for unknown payment methods
      console.warn(
        `Unknown payment method: ${paymentMethod}, defaulting to VNPay format`
      );
      return prepareVNPayOrderData(
        formData,
        cartItems,
        orderTotals,
        locationData
      );
  }
};

/**
 * Get API endpoint dựa trên payment method
 */
export const getPaymentEndpoint = (paymentMethod) => {
  const endpointMap = {
    vnpay_visa: "/wp-json/lianne-vnpay/v1/create-payment",
    paypal: "/wp-json/lianne-paypal/v1/create-payment",
    cod: "/wp-json/wc/v3/orders",
  };

  return (
    endpointMap[paymentMethod] || "/wp-json/lianne-vnpay/v1/create-payment"
  );
};

const CHECKOUT_ERROR_MESSAGES = {
  missing_customer_id: "Vui lòng tải lại trang thanh toán và thử lại.",
  invalid_customer_id: "Thông tin khách hàng không hợp lệ.",
  guest_customer_id_must_be_zero: "Thông tin thanh toán khách không hợp lệ.",
  customer_id_mismatch: "Bạn không có quyền thực hiện thanh toán này.",
  missing_line_items: "Giỏ hàng không có sản phẩm để thanh toán.",
  invalid_line_items: "Một hoặc nhiều sản phẩm không còn hợp lệ.",
  product_not_found: "Sản phẩm không còn tồn tại.",
  product_not_purchasable: "Sản phẩm hiện không thể mua.",
  missing_variation_id: "Vui lòng chọn phân loại sản phẩm.",
  variation_product_mismatch: "Phân loại sản phẩm không còn hợp lệ.",
  variation_attribute_mismatch: "Thuộc tính sản phẩm không còn hợp lệ.",
  invalid_quantity: "Số lượng sản phẩm không hợp lệ.",
  product_out_of_stock: "Sản phẩm đã hết hàng.",
  out_of_stock: "Sản phẩm đã hết hàng.",
  insufficient_stock: "Số lượng sản phẩm trong kho không đủ.",
  invalid_coupon: "Mã giảm giá không hợp lệ.",
  invalid_coupon_code: "Mã giảm giá không hợp lệ.",
  coupon_usage_limit_reached: "Mã giảm giá đã hết lượt sử dụng.",
  checkout_total_mismatch: "Tổng thanh toán đã thay đổi. Vui lòng kiểm tra lại.",
  guest_order_rate_limited: "Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.",
  rate_limited: "Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.",
  duplicate_cod_submission: "Đơn hàng này đã được gửi trước đó.",
  idempotency_key_reused: "Không thể gửi lại yêu cầu thanh toán này.",
  invalid_signature: "Không thể xác minh kết quả thanh toán.",
  invalid_amount: "Không thể xác minh số tiền thanh toán.",
  invalid_transaction_ref: "Không thể xác minh giao dịch.",
  order_not_found: "Không tìm thấy đơn hàng.",
  not_found: "Không tìm thấy thông tin thanh toán.",
  forbidden: "Bạn không có quyền xem thông tin thanh toán này.",
};

export const getCheckoutErrorMessage = (code) =>
  CHECKOUT_ERROR_MESSAGES[code] ||
  "Không thể xử lý thanh toán. Vui lòng thử lại.";

/**
 * Handle checkout response với improved error handling cho cả VNPay và COD
 */
export const handleCheckoutResponse = (response, paymentMethod = "vnpay") => {
  try {

    // Handle COD response (WooCommerce API)
    if (paymentMethod === "cod") {
      return handleCODResponse(response);
    }

    // Handle VNPay response (original logic)
    return handleVNPayResponse(response);
  } catch (error) {
    console.error("Error handling checkout response:", error);
    return {
      success: false,
      errors: ["Failed to process payment response"],
      message: "An error occurred while processing your payment.",
      data: response,
    };
  }
};

/**
 * Handle COD response từ WooCommerce API
 */
export const handleCODResponse = (response) => {
  try {
    // Success must contain the explicit COD contract fields from backend.
    if (
      response.order_id &&
      response.status &&
      response.payment_method === "cod" &&
      Array.isArray(response.checked_out_items)
    ) {
      const orderId = response.order_id;
      const orderNumber = response.order_number || orderId;
      const orderStatus = response.status;

      // Save order info for reference
      localStorage.setItem("cod_last_order_id", orderId.toString());
      localStorage.setItem("cod_last_order_number", orderNumber.toString());

      return {
        success: true,
        orderId: orderId,
        orderNumber: orderNumber,
        orderStatus: orderStatus,
        paymentMethod: "cod",
        total: Number(response.total || 0),
        currency: response.currency || "VND",
        checkedOutItems: response.checked_out_items,
        message: `Đơn hàng #${orderNumber} đã được tạo thành công! Bạn sẽ thanh toán khi nhận hàng.`,
        data: response,
        // COD không cần redirect URL
        redirectUrl: null,
      };
    }

    // Handle WooCommerce error response
    else if (response.success === false && response.code) {
      const errorMessage = getCheckoutErrorMessage(response.code);
      const detailErrors = Array.isArray(response.details)
        ? response.details.map((detail) => getCheckoutErrorMessage(detail.code))
        : [];
      const errors = detailErrors.length > 0 ? detailErrors : [errorMessage];

      return {
        success: false,
        errors: errors,
        message: errorMessage,
        code: response.code,
        details: response.details || [],
        isRateLimited:
          response.code === "rate_limited" ||
          response.code === "guest_order_rate_limited",
        retryAfterMs: Number(response.retry_after_ms || 30000),
        data: response,
      };
    }

    // Unknown response format
    else {
      console.warn("Unexpected COD response format:", response);
      return {
        success: false,
        errors: ["Unexpected response format from server"],
        message: "Unable to process your order. Please try again.",
        data: response,
      };
    }
  } catch (error) {
    console.error("Error handling COD response:", error);
    return {
      success: false,
      errors: ["Failed to process COD order"],
      message: "An error occurred while creating your order.",
      data: response,
    };
  }
};

/**
 * Handle VNPay response (original logic)
 */
export const handleVNPayResponse = (response) => {
  try {
    if (response.id && response.status && !response.success) {
      // This is a WooCommerce order response
      return {
        success: true,
        orderId: response.id,
        orderStatus: response.status,
        message: "Order placed successfully!",
        paymentMethod: response.payment_method || "cod",
        orderKey: response.order_key,
        paymentUrl: response.payment_url,
        data: response,
      };
    }

    // Success response
    if (response.success === true || response.status === "success") {
      const redirectUrl = response.payment_url || response.redirect_url;

      if (redirectUrl) {
        // Save order_id for callback handling
        if (response.order_id) {
          localStorage.setItem("vnpay_last_order_id", response.order_id);
        }

        return {
          success: true,
          redirectUrl: redirectUrl,
          orderId: response.order_id || generateOrderId(),
          orderNumber: response.order_number || response.order_id,
          orderStatus: response.status || "pending",
          paymentMethod: response.payment_method || "vnpay",
          transactionRef: response.transaction_ref,
          lookupToken: response.lookup_token,
          amount: Number(response.amount || 0),
          currency: response.currency || "VND",
          message:
            response.message ||
            "Order placed successfully! Redirecting to payment...",
          data: response,
        };
      } else {
        console.warn(
          "Payment response success but no payment URL provided:",
          response
        );
        return {
          success: false,
          errors: ["Payment URL not provided by payment gateway"],
          message: "Unable to redirect to payment gateway.",
          data: response,
        };
      }
    } else {
      const errorMessage = response.code
        ? getCheckoutErrorMessage(response.code)
        : "Không thể tạo giao dịch thanh toán. Vui lòng thử lại.";
      const errors = [errorMessage];

      return {
        success: false,
        errors: Array.isArray(errors) ? errors : [errors],
        message: errorMessage,
        code: response.code,
        details: response.details || [],
        isRateLimited:
          response.code === "rate_limited" ||
          response.code === "guest_order_rate_limited",
        retryAfterMs: Number(response.retry_after_ms || 30000),
        data: response,
      };
    }
  } catch (error) {
    console.error("Error handling VNPay response:", error);
    return {
      success: false,
      errors: ["Failed to process VNPay response"],
      message: "An error occurred while processing your payment.",
      data: response,
    };
  }
};

/**
 * Auto-detect payment method từ response và handle accordingly
 */
export const handleCheckoutResponseAuto = (response) => {
  try {
    // Detect payment method từ response structure
    let paymentMethod = "vnpay"; // default

    // WooCommerce response có structure: { id, status, payment_method, ... }
    if (response.id && response.status && !response.payment_url) {
      paymentMethod = "cod";
    }
    // VNPay response có structure: { success, payment_url, ... }
    else if (response.payment_url || response.redirect_url) {
      paymentMethod = "vnpay";
    }
    // Check payment_method field if available
    else if (response.payment_method) {
      paymentMethod = response.payment_method;
    }

    return handleCheckoutResponse(response, paymentMethod);
  } catch (error) {
    console.error("Error in auto-detection:", error);
    // Fallback to manual handling
    return handleCheckoutResponse(response, "vnpay");
  }
};

/**
 * Validate địa chỉ location data
 */
export const validateLocationData = (
  selectedProvince,
  selectedDistrict,
  selectedWard
) => {
  const errors = {};

  if (!selectedProvince) {
    errors.province = "Please select a province/city.";
  }

  if (!selectedDistrict) {
    errors.district = "Please select a district.";
  }

  if (!selectedWard) {
    errors.ward = "Please select a ward.";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Re-export existing helpers
export {
  isValidEmail,
  isValidVietnamesePhone,
  generateOrderNote,
  generateOrderId,
  // getCountryCode,
  parseVNPayResponse,
  formatToURLEncoded,
};
